import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Channel {
  readonly channelId: string;
  readonly channelCode: string;
  readonly channelName: string;
  readonly channelType: string;
  readonly isPrivate: boolean;
}

const LINKS: ReadonlyArray<readonly [string, string]> = [
  ['m27.home.linkMessaging', '/admin/collab/messages'],
  ['m27.home.linkDiscussion', '/admin/collab/discussions'],
  ['m27.home.linkKnowledge', '/admin/collab/knowledge'],
  ['m27.home.linkFaq', '/admin/collab/faqs'],
  ['m27.home.linkAnnouncements', '/admin/collab/announcements'],
  ['m27.home.linkBulletin', '/admin/collab/bulletins'],
  ['m27.home.linkAuthoring', '/admin/collab/authoring'],
  ['m27.home.linkReview', '/admin/collab/review'],
  ['m27.home.linkSubscriptions', '/admin/collab/subscriptions'],
  ['m27.home.linkAnalytics', '/admin/collab/analytics'],
];

/** M27 — Collaboration Home (§31). Entry hub: channel directory + quick links. */
export function M27CollaborationHomePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Channel[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: Channel[]; total: number }>('/v1/collab/channels', { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="collaboration-home">
      <h1>{t('m27.home.heading')}</h1>
      <nav aria-label="collab-quick-links">
        <ul>{LINKS.map(([label, href]) => <li key={href}><a href={href}>{t(label)}</a></li>)}</ul>
      </nav>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m27.home.loading')}</p> : (
        <>
          <p aria-label="channel-count">{t('m27.home.count', { count: total })}</p>
          <table aria-label="channel-table">
            <thead><tr><th>{t('m27.home.colCode')}</th><th>{t('m27.home.colName')}</th><th>{t('m27.home.colType')}</th><th>{t('m27.home.colVisibility')}</th></tr></thead>
            <tbody>{rows.map((c) => <tr key={c.channelId}><td>{c.channelCode}</td><td>{c.channelName}</td><td>{c.channelType}</td><td>{c.isPrivate ? t('m27.home.visPrivate') : t('m27.home.visOpen')}</td></tr>)}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
